import { 
    Grid, 
    Box, 
    Typography, 
    Button, 
    TextField,
    Select,
    MenuItem
} from '@mui/material';
import { Search, Add } from '@mui/icons-material';
import { Colors } from '../../../../../styles/theme';
import { sphereValues, cylinderValues, axisValues, addValues, formatForDisplay } from '../../../../../utils/rxValues';

export default function RxSection({ 
    selectedRx,
    setRxSelectDialogOpen,
    setCreateRxDialogOpen,
    rxType = 'glasses' // Default to 'glasses'
}) {
    return (
        <Grid container spacing={1} p={1} boxShadow={2} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1 }}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, backgroundColor: Colors.secondary }}>
                    <Typography sx={{ flexGrow: 1 }}>
                        RX Details: {selectedRx?.rxDate ? `Date: ${selectedRx.rxDate}` : ''} 
                        {selectedRx?.expiryDate ? ` | Expires: ${selectedRx.expiryDate}` : ''}
                        {rxType ? ` | Type: ${rxType.charAt(0).toUpperCase() + rxType.slice(1)}` : ''}
                    </Typography>                    
                    <Button 
                        size='small' 
                        variant='outlined' 
                        startIcon={<Add />} 
                        onClick={() => setCreateRxDialogOpen(true)}
                        sx={{ mr: 1 }}
                    >
                        Create RX
                    </Button>
                    <Button 
                        size='small' 
                        variant='contained' 
                        startIcon={<Search />} 
                        onClick={() => setRxSelectDialogOpen(true)}
                    >
                        Select RX
                    </Button>
                </Box>
            </Grid>
            <Grid item xs={12}>
            <Typography variant='body2' sx={{mb: 1}}>Note: Updating RX values will update the original RX</Typography>
            </Grid>
            <Grid item xs={12}>
                <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>OD</Typography>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={selectedRx?.odSph ? formatForDisplay.sphere(selectedRx.odSph) : ''} // Use formatForDisplay to show +ve values
                    >
                        <MenuItem value="">-</MenuItem>
                        {sphereValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={selectedRx?.odCyl ? formatForDisplay.cylinder(selectedRx.odCyl) : ''} // Use formatForDisplay to show +ve values
                    >
                        <MenuItem value="">-</MenuItem>
                        {cylinderValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={selectedRx?.odAxis ? formatForDisplay.axis(selectedRx.odAxis) : ''} // Ensure axis is 3 digits
                    >
                        <MenuItem value="">-</MenuItem>
                        {axisValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={selectedRx?.odAdd ? formatForDisplay.add(selectedRx.odAdd) : ''} // Use formatForDisplay to show +ve values
                    >
                        <MenuItem value="">-</MenuItem>
                        {addValues.map(val => <MenuItem key={val} value={val}>+{val}</MenuItem>)}
                    </Select>
                    <TextField label="Prism" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.odPrism || ''}  />
                    <TextField label="Base" variant="outlined" size="small" value={selectedRx?.odPrismBase || ''}  />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>OS</Typography>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={selectedRx?.osSph ? formatForDisplay.sphere(selectedRx.osSph) : ''}
                    >
                        <MenuItem value="">-</MenuItem>
                        {sphereValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={selectedRx?.osCyl ? formatForDisplay.cylinder(selectedRx.osCyl) : ''}
                    >
                        <MenuItem value="">-</MenuItem>
                        {cylinderValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={selectedRx?.osAxis ? formatForDisplay.axis(selectedRx.osAxis) : ''}
                    >
                        <MenuItem value="">-</MenuItem>
                        {axisValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                    </Select>
                    <Select
                        displayEmpty
                        size="small"
                        sx={{ mr: 1, minWidth: 80 }}
                        value={selectedRx?.osAdd ? formatForDisplay.add(selectedRx.osAdd) : ''}
                    >
                        <MenuItem value="">-</MenuItem>
                        {addValues.map(val => <MenuItem key={val} value={val}>+{val}</MenuItem>)}
                    </Select>
                    <TextField label="Prism" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.osPrism || ''}  />
                    <TextField label="Base" variant="outlined" size="small" value={selectedRx?.osPrismBase || ''}  />                  
                </Box>
            </Grid>  

            {rxType === 'glasses' && (
                <>
                    <Grid item xs={12}>
                        <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>OD</Typography>
                            <TextField label="Dist PD" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.odDistPd || ''}  />
                            <TextField label="Near PD" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.odNearPd || ''}  />
                            <TextField label="Seg Ht." variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.odSegHt || ''}  />
                            <TextField label="Sp. BC" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.odSpBc || ''}  />
                            <TextField label="Vt. OC" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.odVtOc || ''}  />
                            <TextField label="Lens Dia." variant="outlined" size="small" value={selectedRx?.odLensDiameter || ''}  />
                        </Box>
                    </Grid>               
                    <Grid item xs={12}>
                        <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>OS</Typography>
                            <TextField label="Dist PD" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.osDistPd || ''}  />
                            <TextField label="Near PD" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.osNearPd || ''}  />
                            <TextField label="Seg Ht." variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.osSegHt || ''}  />
                            <TextField label="Sp. BC" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.osSpBc || ''}  />
                            <TextField label="Vt. OC" variant="outlined" sx={{ mr: 1 }} size="small" value={selectedRx?.osVtOc || ''}  />
                            <TextField label="Lens Dia." variant="outlined" size="small" value={selectedRx?.osLensDiameter || ''}  />
                        </Box>
                    </Grid>
                </>
            )}

            {rxType === 'contacts' && (
                <>
                    <Grid item xs={12}>
                        <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>OD</Typography>
                            <TextField label="BC" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.odBc || ''}  />
                            <TextField label="Dia" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.odContactLensDiameter || ''}  />
                            <TextField label="K1" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.odK1 || ''}  />
                            <TextField label="K2" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.odK2 || ''}  />
                            <TextField label="K-Axis" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.odKAxis || ''}  />
                            <TextField label="VA" variant="outlined" sx={{ width: '80px' }} size="small" value={selectedRx?.odVa || ''}  />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display={'flex'} alignItems='center' sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>OS</Typography>
                            <TextField label="BC" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.osBc || ''}  />
                            <TextField label="Dia" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.osContactLensDiameter || ''}  />
                            <TextField label="K1" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.osK1 || ''}  />
                            <TextField label="K2" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.osK2 || ''}  />
                            <TextField label="K-Axis" variant="outlined" sx={{ mr: 1, width: '80px' }} size="small" value={selectedRx?.osKAxis || ''}  />
                            <TextField label="VA" variant="outlined" sx={{ width: '80px' }} size="small" value={selectedRx?.osVa || ''}  />
                        </Box>
                    </Grid>
                </>
            )}
        </Grid>
    );
}